import './App.css';
import { BrowserRouter as Router, Routes, Route } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect, useState } from 'react';
import { MINI_STATS_WIDGET_ROUTE, PREMATCH_WIDGET_ROUTE } from '@/common/constants';
import { getIsInIframe } from '@/common/utils';
import {
  StatsConsoleWrapper,
  TestWidgetsView,
  TestWidgetsExamplesList,
  ThemeProvider,
  ColorProvider,
  NotFound,
  type IState,
  initialStateContext,
  StateContext,
  TimelinePlayer,
  VisualistaionPlayer,
  Test,
  TestColorWidget,
  ErrorBoundary,
  TranslationProvider,
  DataOrchestratorProvider,
} from '@/ihs-widget';
import PrematchWidget from '@/prematch-widget';
import MiniStatsWidget from '@/prematch-widget/containers/MiniStatsWidget/MiniStatsWidget';

/**
 * Aligns with Vite `base`: root deploy uses `/`; prematch uses `/prematch/` and strips the final slash via `.replace(/\/$/, '')`.
 */
const routerBasename =
  import.meta.env.BASE_URL === '/' ? undefined : import.meta.env.BASE_URL.replace(/\/$/, '');

const queryClient = new QueryClient();

const App = () => {
  const isInIframe = getIsInIframe();
  const [stContext, setStContext] = useState<IState>(initialStateContext);

  useEffect(() => {
    const htmlElement = document.documentElement;
    htmlElement.classList.toggle('preview-grid', !isInIframe);

    return () => {
      htmlElement.classList.remove('preview-grid');
    };
  }, [isInIframe]);

  return (
    <ErrorBoundary>
      <Router basename={routerBasename}>
        <StateContext.Provider value={{ stContext, setStContext }}>
          <ThemeProvider>
            <DataOrchestratorProvider>
              <ColorProvider>
                <TranslationProvider>
                  <QueryClientProvider client={queryClient}>
                    {!isInIframe && <ReactQueryDevtools initialIsOpen={false} />}
                    <Routes>
                      <Route path="/" element={<TestWidgetsExamplesList />} />
                      <Route path="/generator" element={<TestWidgetsView />} />
                      <Route path="*" element={<NotFound />} />
                      <Route
                        path="/StatsConsole/:locale/"
                        element={<StatsConsoleWrapper item={'StatsConsole'} />}
                      />
                      <Route
                        path="/StatsConsole/:locale/:state"
                        element={<StatsConsoleWrapper item={'StatsConsole'} />}
                      />
                      <Route path="/TimelinePlayer/" element={<TimelinePlayer />} />
                      <Route path="/TimelinePlayer/:locale/" element={<TimelinePlayer />} />
                      <Route path="/TimelinePlayer/:locale/:state" element={<TimelinePlayer />} />
                      <Route path="/VisualistaionPlayer/" element={<VisualistaionPlayer />} />
                      <Route
                        path="/VisualistaionPlayer/:locale/"
                        element={<VisualistaionPlayer />}
                      />
                      <Route
                        path="/VisualistaionPlayer/:locale/:state"
                        element={<VisualistaionPlayer />}
                      />
                      <Route path="/test" element={<Test />} />
                      <Route path="/test-color" element={<TestColorWidget />} />
                      <Route path={PREMATCH_WIDGET_ROUTE} element={<PrematchWidget />} />
                      <Route
                        path={`${PREMATCH_WIDGET_ROUTE}/:locale/`}
                        element={<PrematchWidget />}
                      />
                      <Route
                        path={`${PREMATCH_WIDGET_ROUTE}}/:locale/:state`}
                        element={<PrematchWidget />}
                      />
                      <Route path={MINI_STATS_WIDGET_ROUTE} element={<MiniStatsWidget />} />
                      <Route
                        path={`${MINI_STATS_WIDGET_ROUTE}/:locale/`}
                        element={<MiniStatsWidget />}
                      />
                    </Routes>
                  </QueryClientProvider>
                </TranslationProvider>
              </ColorProvider>
            </DataOrchestratorProvider>
          </ThemeProvider>
        </StateContext.Provider>
      </Router>
    </ErrorBoundary>
  );
};

export default App;
